import { useTrade } from "@/contexts/TradeContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Briefcase, Check, ChevronDown, Settings2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * Top-bar switcher that shows the org's active trade and lets multi-trade
 * orgs flip between them. Hidden entirely if the org only has one trade.
 */
export function TradeSwitcher() {
  const { orgTrades, activeTradeCode, setActiveTradeCode, loading } = useTrade();
  const navigate = useNavigate();

  if (loading || orgTrades.length === 0) return null;

  const active = orgTrades.find((t) => t.trade_code === activeTradeCode);

  // Single-trade orgs: show static badge with link to settings to add more
  if (orgTrades.length === 1) {
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate("/settings?tab=trades")}
        className="hidden md:flex h-9 gap-2 rounded-xl border border-border/80 bg-card px-3 text-xs text-foreground shadow-sm"
      >
        <Briefcase className="h-3.5 w-3.5" />
        <span>{active?.name ?? "Trade"}</span>
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 gap-2 rounded-xl border-border/80 bg-card text-foreground shadow-sm hover:bg-accent/80">
          <Briefcase className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">{active?.name ?? "Select trade"}</span>
          <ChevronDown className="h-3.5 w-3.5 text-foreground/80" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Active trade</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {orgTrades.map((t) => (
          <DropdownMenuItem
            key={t.trade_code}
            onClick={() => setActiveTradeCode(t.trade_code)}
            className="flex items-center justify-between"
          >
            <span>{t.name}</span>
            {t.trade_code === activeTradeCode && <Check className="h-4 w-4" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/settings?tab=trades")}>
          <Settings2 className="h-4 w-4 mr-2" />
          Manage trades
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
